import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { Box, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EColumnKind, POSITION_COLUMN } from "./types";
import { formatCell, formatInteger } from "./lib/format";
/** Rows shown at once; a table of this many or fewer has no pager. */
export var DATA_TABLE_PAGE_SIZE = 100;
/** Page sizes the viewer can pick; the smallest is the default. */
export var DATA_TABLE_PAGE_SIZES = [DATA_TABLE_PAGE_SIZE, 250, 1000];
/**
 * A widget's query result with exact values; the renderer's `position` column
 * is left out. A table longer than a page is shown a page at a time, so that
 * thousands of rows do not all become part of the document at once.
 */
export function MonitoringDataTable(_a) {
    var table = _a.table, caption = _a.caption, _b = _a.columnLabel, columnLabel = _b === void 0 ? function (name) { return name; } : _b;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var _d = useState(0), page = _d[0], setPage = _d[1];
    var _e = useState(DATA_TABLE_PAGE_SIZE), pageSize = _e[0], setPageSize = _e[1];
    var shown = table.columns
        .map(function (column, index) { return ({ column: column, index: index }); })
        .filter(function (_a) {
        var column = _a.column;
        return column.name !== POSITION_COLUMN;
    });
    if (!table.rows.length) {
        return _jsx(Typography, { color: "text.secondary", children: t("monitoring.dataTable.empty") });
    }
    var paged = table.rows.length > DATA_TABLE_PAGE_SIZE;
    // A refresh can bring fewer rows: the last page there is, never an empty one.
    var lastPage = Math.max(0, Math.ceil(table.rows.length / pageSize) - 1);
    var current = paged ? Math.min(page, lastPage) : 0;
    var rows = paged ? table.rows.slice(current * pageSize, (current + 1) * pageSize) : table.rows;
    return (_jsxs(Box, { sx: { overflowX: "auto" }, children: [_jsxs(Table, { size: "small", "aria-label": caption, children: [_jsx(TableHead, { children: _jsx(TableRow, { children: shown.map(function (_a) {
                                var column = _a.column;
                                return (_jsx(TableCell, { align: column.kind === EColumnKind.TEXT ? "left" : "right", children: columnLabel(column.name) }, column.name));
                            }) }) }), _jsx(TableBody, { children: rows.map(function (row, rowIndex) { return (_jsx(TableRow, { children: shown.map(function (_a) {
                                var column = _a.column, index = _a.index;
                                return (_jsx(TableCell, { align: column.kind === EColumnKind.TEXT ? "left" : "right", sx: { fontVariantNumeric: "tabular-nums" }, children: formatCell(row[index], column.kind, i18n.language) }, column.name));
                            }) }, current * pageSize + rowIndex)); }) })] }), paged ? (_jsx(TablePagination, { component: "div", count: table.rows.length, page: current, rowsPerPage: pageSize, rowsPerPageOptions: DATA_TABLE_PAGE_SIZES, onPageChange: function (_event, next) { return setPage(next); }, onRowsPerPageChange: function (event) {
                    setPageSize(Number(event.target.value));
                    setPage(0);
                }, showFirstButton: true, showLastButton: true, labelRowsPerPage: t("monitoring.dataTable.rowsPerPage"), labelDisplayedRows: function (_a) {
                    var from = _a.from, to = _a.to, count = _a.count;
                    return t("monitoring.dataTable.shownRows", {
                        from: formatInteger(from, i18n.language),
                        to: formatInteger(to, i18n.language),
                        total: formatInteger(count, i18n.language),
                    });
                }, getItemAriaLabel: function (type) { return t("monitoring.dataTable.".concat(type, "Page")); } })) : null] }));
}
